// =====================================================================
// ScribePanel (Clara Scribe Fase A / S61) — painel "🎙️ Clara Scribe" dentro
// do EncounterForm. Grava a consulta (MediaRecorder) -> envia -> dispara o
// ASR+SOAP no backend -> preenche o rascunho SOAP no formulário (onDraft).
//
// 🔒 LGPD / CFM 2.454:
//   - Consentimento é PRÉ-REQUISITO (checkbox trava a gravação).
//   - A IA só RASCUNHA — a médica revisa, edita e ASSINA (fluxo normal).
//   - Sem chave (ASR/LLM) o backend responde "não configurado" e o painel
//     mostra o aviso (estado LOCAL antes do deploy + DPA).
// =====================================================================
import React, { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  TextField,
  MenuItem
} from "@material-ui/core";
import MicIcon from "@material-ui/icons/Mic";
import StopIcon from "@material-ui/icons/Stop";
import LockIcon from "@material-ui/icons/Lock";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ReplayIcon from "@material-ui/icons/Replay";
import LocalPharmacyIcon from "@material-ui/icons/LocalPharmacy";
import DescriptionIcon from "@material-ui/icons/Description";
import AssignmentIcon from "@material-ui/icons/Assignment";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import {
  scribeConfigStatus,
  createScribeSession,
  uploadScribeAudio,
  processScribeSession,
  discardScribeSession,
  scribePrescriptionSuggestions,
  scribeErrorMessage
} from "../../services/scribeService";
import { emitDocument } from "../../services/fileService";
import { getPatient, updatePatient } from "../../services/patientService";

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`
  },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer"
  },
  title: { fontWeight: 600, flex: 1 },
  body: { marginTop: theme.spacing(1.5) },
  micro: { color: theme.palette.text.secondary, fontSize: 12, marginTop: 4 },
  row: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  rec: { color: "#c62828", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  seal: {
    background: "#fff8e1",
    color: "#8d6e00",
    border: "1px solid #ffe082",
    borderRadius: 6,
    padding: theme.spacing(0.75, 1),
    fontSize: 12,
    marginTop: theme.spacing(1)
  },
  transcript: {
    whiteSpace: "pre-wrap",
    background: theme.palette.action.hover,
    borderRadius: 6,
    padding: theme.spacing(1.25),
    fontSize: 13,
    marginTop: theme.spacing(1),
    maxHeight: 220,
    overflowY: "auto"
  },
  err: { color: "#c62828", marginTop: theme.spacing(1) },
  faseB: { marginTop: theme.spacing(1) },
  confRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(0.75)
  },
  confLabel: { fontSize: 12, color: theme.palette.text.secondary, marginRight: 4 },
  rxBox: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    background: theme.palette.action.hover,
    borderRadius: 6
  },
  rxItem: { marginBottom: theme.spacing(0.75) },
  rxList: { margin: "4px 0 0 0", paddingLeft: 18, fontSize: 13 },
  acoes: { marginTop: theme.spacing(1), paddingTop: theme.spacing(1), borderTop: `1px dashed ${theme.palette.divider}` },
  acaoItem: { marginTop: theme.spacing(1), padding: theme.spacing(1), background: theme.palette.action.hover, borderRadius: 6 },
  avisoProf: {
    background: "#fff3e0",
    color: "#e65100",
    border: "1px solid #ffb74d",
    borderRadius: 6,
    padding: theme.spacing(1),
    fontSize: 13,
    marginTop: theme.spacing(0.75),
    fontWeight: 500
  }
}));

// cor do chip de confiança por campo (alta=verde, média=âmbar, baixa=vermelho)
const confStyle = (c) =>
  c === "alta"
    ? { background: "#e8f5e9", color: "#2e7d32" }
    : c === "baixa"
    ? { background: "#ffebee", color: "#c62828" }
    : { background: "#fff8e1", color: "#8d6e00" };

// Fase B+ "ações": tipo do LLM (PT-BR) -> documentType do backend + rótulo.
const DOC_TYPE_MAP = {
  atestado: "medical_certificate",
  pedido_exame: "exam_request",
  encaminhamento: "referral",
  receita: "prescription"
};
const DOC_TYPE_LABEL = {
  atestado: "Atestado",
  pedido_exame: "Pedido de exame",
  encaminhamento: "Encaminhamento",
  receita: "Receita"
};

// Fase B+ "ficha": campo da sugestão da IA (PT-BR) -> campo do paciente (model) + rótulo.
const FICHA_FIELDS = [
  { key: "allergies", sug: "alergias", label: "Alergias" },
  { key: "medicationsCurrent", sug: "medicamentosEmUso", label: "Medicações em uso" },
  { key: "personalHistory", sug: "antecedentesPessoais", label: "Antecedentes pessoais" },
  { key: "familyHistory", sug: "antecedentesFamiliares", label: "Antecedentes familiares" }
];

const fmtTime = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return "";
  const cands = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  for (let i = 0; i < cands.length; i++) {
    try { if (MediaRecorder.isTypeSupported(cands[i])) return cands[i]; } catch (e) { /* noop */ }
  }
  return "";
};

const extFor = (type) =>
  String(type).indexOf("ogg") >= 0 ? "ogg" : String(type).indexOf("mp4") >= 0 ? "m4a" : "webm";

const ScribePanel = ({
  patientId,
  professionalId,
  professionals = [],
  onProfessionalChange,
  encounterId,
  onDraft
}) => {
  const classes = useStyles();
  const [config, setConfig] = useState(null); // { asr, llm, provider }
  const [open, setOpen] = useState(false);
  const [consent, setConsent] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle|recording|busy|drafted|error
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [session, setSession] = useState(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [rx, setRx] = useState(null); // sugestões de receita (Fase B)
  const [rxLoading, setRxLoading] = useState(false);
  const [docDrafts, setDocDrafts] = useState([]); // ações: documentos sugeridos (Fase B+)
  const [fichaSug, setFichaSug] = useState(null); // sugestões pra ficha (alergias/meds/antecedentes)
  const [fichaForm, setFichaForm] = useState({ allergies: "", medicationsCurrent: "", personalHistory: "", familyHistory: "" });
  const [fichaStatus, setFichaStatus] = useState("idle"); // idle|aplicando|ok|erro
  const [resumo, setResumo] = useState(""); // resumo pro paciente (editável)
  const [copyOk, setCopyOk] = useState(false);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopAll = () => {
    try { if (timerRef.current) clearInterval(timerRef.current); } catch (e) { /* noop */ }
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { /* noop */ }
    try { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ }
  };

  useEffect(() => {
    let active = true;
    scribeConfigStatus()
      .then((c) => { if (active) setConfig(c); })
      .catch(() => { if (active) setConfig({ asr: false, llm: false, provider: null }); });
    return () => { active = false; stopAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notReady = config && (!config.asr || !config.llm);

  const startRecording = async () => {
    setErrorMsg("");
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || typeof MediaRecorder === "undefined") {
      setErrorMsg("Este navegador não suporta gravação de áudio.");
      setPhase("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const type = rec.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        handleUpload(blob, type);
      };
      recorderRef.current = rec;
      rec.start();
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
      setPhase("recording");
    } catch (err) {
      setErrorMsg("Não foi possível acessar o microfone (permissão negada?).");
      setPhase("error");
    }
  };

  const stopRecording = () => {
    try { if (timerRef.current) clearInterval(timerRef.current); } catch (e) { /* noop */ }
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { /* noop */ }
    try { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ }
    setPhase("busy");
  };

  const handleUpload = async (blob, type) => {
    try {
      setPhase("busy");
      const sess = await createScribeSession({
        patientId: patientId || undefined,
        professionalId: professionalId || undefined,
        encounterId: encounterId || undefined,
        consentObtained: true
      });
      await uploadScribeAudio(sess.id, blob, `consulta-${sess.id}.${extFor(type)}`);
      const processed = await processScribeSession(sess.id);
      setSession(processed);
      if (processed.status === "drafted" && processed.soapDraft) {
        setPhase("drafted");
        const acoes = processed.soapDraft.acoesSugeridas || {};
        setDocDrafts(
          Array.isArray(acoes.documentos) ? acoes.documentos.map((d) => ({ ...d, status: "idle" })) : []
        );
        setResumo(processed.soapDraft.resumoPaciente || "");
        const fs = processed.soapDraft.fichaSugerida || {};
        setFichaSug(fs);
        const hasFicha = !!(fs && (fs.alergias || fs.medicamentosEmUso || fs.antecedentesPessoais || fs.antecedentesFamiliares));
        if (hasFicha && patientId) {
          // carrega a ficha ATUAL pra merge seguro (nunca sobrescreve sem o médico ver)
          try {
            const p = await getPatient(patientId);
            setFichaForm({
              allergies: p.allergies || "",
              medicationsCurrent: p.medicationsCurrent || "",
              personalHistory: p.personalHistory || "",
              familyHistory: p.familyHistory || ""
            });
          } catch (e) {
            setFichaForm({ allergies: "", medicationsCurrent: "", personalHistory: "", familyHistory: "" });
          }
        }
        if (onDraft) onDraft(processed.soapDraft, processed.id);
      } else {
        setPhase("error");
        setErrorMsg(processed.errorMessage || "A transcrição não pôde ser concluída.");
      }
    } catch (err) {
      setPhase("error");
      setErrorMsg(scribeErrorMessage(err));
    }
  };

  const reset = async () => {
    if (session && session.id && session.status !== "applied") {
      try { await discardScribeSession(session.id); } catch (e) { /* best-effort */ }
    }
    setSession(null);
    setPhase("idle");
    setElapsed(0);
    setErrorMsg("");
    setShowTranscript(false);
    setRx(null);
    setDocDrafts([]);
    setFichaSug(null);
    setFichaForm({ allergies: "", medicationsCurrent: "", personalHistory: "", familyHistory: "" });
    setFichaStatus("idle");
    setResumo("");
    setCopyOk(false);
  };

  const loadRx = async () => {
    if (!session || !session.id) return;
    setRxLoading(true);
    try {
      const data = await scribePrescriptionSuggestions(session.id);
      setRx(data || { suggestions: [] });
    } catch (e) {
      setRx({ suggestions: [] });
    } finally {
      setRxLoading(false);
    }
  };

  // Gera o PDF de um documento sugerido (reusa o emit-document do EHR).
  const generateDoc = async (i) => {
    const d = docDrafts[i];
    if (!d || !d.corpo || !d.corpo.trim()) return;
    if (!professionalId) return; // exige o profissional (à prova de falha)
    setDocDrafts((prev) => prev.map((x, j) => (j === i ? { ...x, status: "gerando" } : x)));
    try {
      await emitDocument(patientId, {
        documentType: DOC_TYPE_MAP[d.tipo] || "medical_certificate",
        title: d.titulo || undefined,
        body: d.corpo,
        professionalId: professionalId || undefined
      });
      setDocDrafts((prev) => prev.map((x, j) => (j === i ? { ...x, status: "ok" } : x)));
    } catch (e) {
      setDocDrafts((prev) => prev.map((x, j) => (j === i ? { ...x, status: "erro" } : x)));
    }
  };

  // Anexa a sugestão da IA ao campo da ficha (append — nunca sobrescreve).
  const addSug = (key, text) => {
    if (!text) return;
    setFichaForm((prev) => {
      const cur = (prev[key] || "").trim();
      return { ...prev, [key]: cur ? `${cur}\n${text}` : text };
    });
  };

  // Aplica a ficha (revisada/editada) ao paciente — update PARCIAL, só os 4 campos.
  const applyFicha = async () => {
    if (!patientId) return;
    setFichaStatus("aplicando");
    try {
      await updatePatient(patientId, {
        allergies: fichaForm.allergies,
        medicationsCurrent: fichaForm.medicationsCurrent,
        personalHistory: fichaForm.personalHistory,
        familyHistory: fichaForm.familyHistory
      });
      setFichaStatus("ok");
    } catch (e) {
      setFichaStatus("erro");
    }
  };

  const copyResumo = async () => {
    try {
      await navigator.clipboard.writeText(resumo || "");
      setCopyOk(true);
      setTimeout(() => setCopyOk(false), 2000);
    } catch (e) {
      /* clipboard pode falhar sem permissão; ignore */
    }
  };

  return (
    <Paper className={classes.panel} variant="outlined">
      <div className={classes.head} onClick={() => setOpen((o) => !o)}>
        <RecordVoiceOverIcon color="primary" />
        <Typography className={classes.title}>🎙️ Clara Scribe — rascunho do SOAP por IA</Typography>
        <Chip
          size="small"
          label={notReady ? "Aguardando configuração" : "Disponível"}
          style={
            notReady
              ? { background: "#fff3e0", color: "#e65100", border: "1px solid #ffcc80" }
              : { background: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" }
          }
        />
        <Button size="small">{open ? "Fechar" : "Abrir"}</Button>
      </div>

      <Collapse in={open}>
        <div className={classes.body}>
          {notReady && (
            <Typography className={classes.micro}>
              <LockIcon fontSize="inherit" style={{ verticalAlign: "middle", marginRight: 4 }} />
              Recurso ainda <strong>aguardando configuração</strong> (chaves do ASR/Claude + DPA). Decisões em
              <em> PME_SCRIBE_DECISOES_E_KIT_S61.md</em> (D2/D3). A gravação só fica ativa após isso.
            </Typography>
          )}

          <FormControlLabel
            control={<Checkbox checked={consent} onChange={(e) => setConsent(e.target.checked)} color="primary" size="small" />}
            label={
              <span style={{ fontSize: 13 }}>
                O paciente foi informado e <strong>autorizou</strong> a gravação da consulta assistida por IA para
                documentação do prontuário (LGPD Art. 11). O áudio é apagado após a transcrição. O médico é o
                responsável final.
              </span>
            }
          />

          <div className={classes.row}>
            {phase === "recording" ? (
              <>
                <Button variant="contained" color="secondary" startIcon={<StopIcon />} onClick={stopRecording}>
                  Parar
                </Button>
                <span className={classes.rec}>● {fmtTime(elapsed)}</span>
              </>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<MicIcon />}
                onClick={startRecording}
                disabled={!consent || notReady || phase === "busy"}
              >
                Iniciar gravação
              </Button>
            )}

            {phase === "busy" && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CircularProgress size={18} /> Transcrevendo… gerando SOAP…
              </span>
            )}

            {phase === "drafted" && (
              <>
                <Chip size="small" label="✅ Rascunho aplicado ao formulário" style={{ background: "#e8f5e9", color: "#2e7d32" }} />
                {session && session.transcript && (
                  <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setShowTranscript((s) => !s)}>
                    {showTranscript ? "Ocultar transcrição" : "Ver transcrição"}
                  </Button>
                )}
                <Button size="small" startIcon={<ReplayIcon />} onClick={reset}>
                  Nova gravação
                </Button>
              </>
            )}

            {phase === "error" && (
              <Button size="small" startIcon={<ReplayIcon />} onClick={reset}>
                Tentar de novo
              </Button>
            )}
          </div>

          {phase === "drafted" && (
            <>
              <div className={classes.seal}>
                🤖 Rascunho gerado com apoio de IA — <strong>revise e edite</strong> os campos abaixo antes de assinar. A
                responsável final pelo conteúdo é a médica.
              </div>

              {session && session.soapDraft && (
                <div className={classes.faseB}>
                  {session.soapDraft.confiancaCampos &&
                    Object.keys(session.soapDraft.confiancaCampos).length > 0 && (
                      <div className={classes.confRow}>
                        <span className={classes.confLabel}>Confiança:</span>
                        {[
                          ["subjective", "S"],
                          ["objective", "O"],
                          ["assessment", "A"],
                          ["plan", "P"]
                        ].map(([k, l]) => {
                          const c = session.soapDraft.confiancaCampos[k];
                          return c ? (
                            <Chip key={k} size="small" label={`${l}: ${c}`} style={confStyle(c)} />
                          ) : null;
                        })}
                      </div>
                    )}

                  {Array.isArray(session.soapDraft.cidSugerido) &&
                    session.soapDraft.cidSugerido.length > 0 && (
                      <div className={classes.confRow}>
                        <span className={classes.confLabel}>CID-10 sugerido:</span>
                        {session.soapDraft.cidSugerido.map((c, i) => (
                          <Chip
                            key={i}
                            size="small"
                            variant="outlined"
                            label={
                              typeof c === "string"
                                ? c
                                : `${c.codigo}${c.descricao ? " — " + c.descricao : ""}`
                            }
                          />
                        ))}
                      </div>
                    )}

                  {Array.isArray(session.soapDraft.receitaSugerida) &&
                    session.soapDraft.receitaSugerida.length > 0 && (
                      <div className={classes.confRow}>
                        <Button
                          size="small"
                          startIcon={<LocalPharmacyIcon />}
                          onClick={loadRx}
                          disabled={rxLoading}
                        >
                          {rxLoading
                            ? "Buscando no receituário…"
                            : `Sugestões de receita (${session.soapDraft.receitaSugerida.length})`}
                        </Button>
                      </div>
                    )}

                  {rx && (
                    <div className={classes.rxBox}>
                      {rx.suggestions.length === 0 && (
                        <Typography variant="caption" color="textSecondary">
                          Nenhuma sugestão de receita no rascunho.
                        </Typography>
                      )}
                      {rx.suggestions.map((s, i) => (
                        <div key={i} className={classes.rxItem}>
                          <Typography variant="body2">
                            <strong>{s.nome}</strong>
                            {s.motivo ? ` — ${s.motivo}` : ""}
                          </Typography>
                          {s.matches && s.matches.length > 0 ? (
                            <ul className={classes.rxList}>
                              {s.matches.map((m) => (
                                <li key={m.id}>
                                  <strong>{m.displayName}</strong>
                                  {m.folder ? ` · ${m.folder}` : ""}
                                  {m.posology ? ` · ${m.posology}` : ""}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              — sem correspondência no receituário (use o picker da Receita)
                            </Typography>
                          )}
                        </div>
                      ))}
                      <Typography variant="caption" color="textSecondary">
                        Sugestões do receituário da clínica — a médica escolhe e insere pela Receita. A IA não prescreve.
                      </Typography>
                    </div>
                  )}

                  {(docDrafts.length > 0 ||
                    (session.soapDraft.acoesSugeridas &&
                      session.soapDraft.acoesSugeridas.retorno &&
                      session.soapDraft.acoesSugeridas.retorno.texto)) && (
                    <div className={classes.acoes}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        📋 Ações sugeridas
                      </Typography>
                      {!professionalId && (
                        <div className={classes.avisoProf}>
                          ⚠️ <strong>Selecione o profissional que assina</strong> abaixo antes de gerar os documentos —
                          senão eles saem <strong>sem nome e sem CRM</strong>. Os botões "Gerar" ficam bloqueados até você escolher.
                        </div>
                      )}
                      <TextField
                        select
                        label="Profissional que assina os documentos"
                        value={professionalId || ""}
                        onChange={(e) => onProfessionalChange && onProfessionalChange(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                        style={{ marginTop: 6 }}
                        error={!professionalId}
                        helperText={
                          !professionalId
                            ? "⚠️ Selecione o profissional para o documento sair com nome + CRM"
                            : "Sincroniza com o campo Profissional da consulta."
                        }
                      >
                        <MenuItem value="">—</MenuItem>
                        {professionals.map((p) => (
                          <MenuItem key={p.id} value={String(p.id)}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      {docDrafts.map((d, i) => (
                        <div key={i} className={classes.acaoItem}>
                          <Typography variant="body2">
                            <strong>{DOC_TYPE_LABEL[d.tipo] || d.tipo}</strong>
                            {d.titulo ? ` — ${d.titulo}` : ""}
                          </Typography>
                          <TextField
                            multiline
                            minRows={2}
                            fullWidth
                            size="small"
                            variant="outlined"
                            value={d.corpo || ""}
                            onChange={(e) =>
                              setDocDrafts((prev) =>
                                prev.map((x, j) => (j === i ? { ...x, corpo: e.target.value } : x))
                              )
                            }
                            disabled={d.status === "gerando" || d.status === "ok"}
                            style={{ marginTop: 4 }}
                          />
                          <div style={{ marginTop: 4 }}>
                            {d.status === "ok" ? (
                              <Chip
                                size="small"
                                label="✓ PDF gerado — veja na aba Documentos"
                                style={{ background: "#e8f5e9", color: "#2e7d32" }}
                              />
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DescriptionIcon />}
                                onClick={() => generateDoc(i)}
                                disabled={d.status === "gerando" || !professionalId}
                              >
                                {d.status === "gerando" ? "Gerando…" : `Gerar ${DOC_TYPE_LABEL[d.tipo] || "documento"}`}
                              </Button>
                            )}
                            {d.status === "erro" && (
                              <Typography variant="caption" style={{ color: "#c62828", marginLeft: 8 }}>
                                falhou — tente de novo
                              </Typography>
                            )}
                          </div>
                        </div>
                      ))}
                      {session.soapDraft.acoesSugeridas &&
                        session.soapDraft.acoesSugeridas.retorno &&
                        session.soapDraft.acoesSugeridas.retorno.texto && (
                          <Typography variant="body2" style={{ marginTop: 6 }}>
                            📅 <strong>Retorno:</strong> {session.soapDraft.acoesSugeridas.retorno.texto}{" "}
                            <span style={{ color: "#888", fontSize: 12 }}>— agende na Agenda Médica</span>
                          </Typography>
                        )}
                    </div>
                  )}

                  {fichaSug &&
                    (fichaSug.alergias ||
                      fichaSug.medicamentosEmUso ||
                      fichaSug.antecedentesPessoais ||
                      fichaSug.antecedentesFamiliares) && (
                      <div className={classes.acoes}>
                        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                          📋 Ficha do paciente — sugestões da IA
                        </Typography>
                        <Typography variant="caption" color="textSecondary" component="div">
                          A IA captou dados persistentes da consulta. Os campos já vêm com o que está na
                          ficha — revise/edite e aplique. <strong>Nada é sobrescrito sem você ver.</strong>
                        </Typography>
                        {FICHA_FIELDS.map((f) => {
                          const sug = fichaSug[f.sug];
                          const cur = fichaForm[f.key] || "";
                          const already = sug && cur.indexOf(sug) >= 0;
                          return (
                            <div key={f.key} className={classes.acaoItem}>
                              <Typography variant="body2">
                                <strong>{f.label}</strong>
                              </Typography>
                              {sug ? (
                                <div style={{ margin: "4px 0", fontSize: 12 }}>
                                  <span style={{ color: "#6a1b9a" }}>💡 IA ouviu: «{sug}»</span>{" "}
                                  {already ? (
                                    <span style={{ color: "#2e7d32" }}>✓ incluído</span>
                                  ) : (
                                    <Button
                                      size="small"
                                      onClick={() => addSug(f.key, sug)}
                                      style={{ minWidth: 0, padding: "0 6px" }}
                                      disabled={fichaStatus === "aplicando" || fichaStatus === "ok"}
                                    >
                                      + adicionar
                                    </Button>
                                  )}
                                </div>
                              ) : null}
                              <TextField
                                multiline
                                minRows={1}
                                fullWidth
                                size="small"
                                variant="outlined"
                                value={cur}
                                onChange={(e) =>
                                  setFichaForm((prev) => ({ ...prev, [f.key]: e.target.value }))
                                }
                                disabled={fichaStatus === "aplicando" || fichaStatus === "ok"}
                              />
                            </div>
                          );
                        })}
                        <div style={{ marginTop: 8 }}>
                          {fichaStatus === "ok" ? (
                            <Chip
                              size="small"
                              label="✓ Ficha do paciente atualizada"
                              style={{ background: "#e8f5e9", color: "#2e7d32" }}
                            />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AssignmentIcon />}
                              onClick={applyFicha}
                              disabled={fichaStatus === "aplicando" || !patientId}
                            >
                              {fichaStatus === "aplicando" ? "Aplicando…" : "Aplicar à ficha do paciente"}
                            </Button>
                          )}
                          {fichaStatus === "erro" && (
                            <Typography variant="caption" style={{ color: "#c62828", marginLeft: 8 }}>
                              falhou — tente de novo
                            </Typography>
                          )}
                        </div>
                      </div>
                    )}

                  {resumo ? (
                    <div className={classes.acoes}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                        📝 Resumo pro paciente
                      </Typography>
                      <Typography variant="caption" color="textSecondary" component="div">
                        Linguagem simples pra entregar/enviar ao paciente. Revise antes de usar.
                      </Typography>
                      <TextField
                        multiline
                        minRows={3}
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={resumo}
                        onChange={(e) => setResumo(e.target.value)}
                        style={{ marginTop: 6 }}
                      />
                      <div style={{ marginTop: 6 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<FileCopyIcon />}
                          onClick={copyResumo}
                        >
                          {copyOk ? "Copiado!" : "Copiar"}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              <Collapse in={showTranscript}>
                <Divider style={{ margin: "8px 0" }} />
                <Typography variant="caption" color="textSecondary">Transcrição da consulta:</Typography>
                <div className={classes.transcript}>{session && session.transcript}</div>
              </Collapse>
            </>
          )}

          {errorMsg && <Typography className={classes.err} variant="body2">{errorMsg}</Typography>}
        </div>
      </Collapse>
    </Paper>
  );
};

export default ScribePanel;
